import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../features/auth/hooks/useAuth';

export const MODES = ['mention', 'recognition', 'absentee'];
export const DEFAULT_MODE = 'mention';
const LEGACY_KEY = 'disciplineQueueRecognitionMode';

const ContextModeContext = createContext(null);

/**
 * Read mode from localStorage for the given key.
 * Performs a one-time migration from the legacy disciplineQueueRecognitionMode key
 * if no value is stored under the new key.
 */
const readAndMigrate = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored && MODES.includes(stored)) return stored;

    // Not found in new key — check legacy key for one-time migration
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) {
      const migrated = legacy === 'true' ? 'recognition' : 'mention';
      localStorage.setItem(key, migrated);
      localStorage.removeItem(LEGACY_KEY);
      return migrated;
    }

    return DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

/**
 * ContextModeProvider — owns the global mode (mention | recognition | absentee).
 *
 * Mode persists to localStorage under swan_mode_<uid> (per-user, same pattern
 * as ScopeContext). Falls back to swan_mode when no auth user is available yet.
 *
 * On first read, migrates the legacy disciplineQueueRecognitionMode key so
 * existing users don't lose their last-used mode on upgrade.
 */
export const ContextModeProvider = ({ children }) => {
  const { user } = useAuth();

  const modeStorageKey = user?.uid ? `swan_mode_${user.uid}` : 'swan_mode';

  const [mode, setModeRaw] = useState(() => readAndMigrate(modeStorageKey));

  // Re-read localStorage when the user changes (key changes)
  useEffect(() => {
    const resolved = readAndMigrate(modeStorageKey);
    setModeRaw(resolved);
  }, [modeStorageKey]);

  const setMode = useCallback(
    (next) => {
      if (!MODES.includes(next)) return; // Ignore invalid values
      setModeRaw(next);
      try {
        localStorage.setItem(modeStorageKey, next);
        // Remove legacy key if still present (belt-and-suspenders migration cleanup)
        localStorage.removeItem(LEGACY_KEY);
      } catch {
        // Fail silently (private browsing, storage quota, etc.)
      }
    },
    [modeStorageKey]
  );

  return (
    <ContextModeContext.Provider value={{ mode, setMode, modes: MODES }}>
      {children}
    </ContextModeContext.Provider>
  );
};

/**
 * useMode — hook to access the current mode context.
 *
 * Returns a stable fallback when used outside ContextModeProvider so callers
 * do not crash during unit tests or SSR-style renders.
 */
export const useMode = () => {
  const context = useContext(ContextModeContext);
  if (!context) {
    // Defensive fallback — not inside ContextModeProvider
    return { mode: DEFAULT_MODE, setMode: () => {}, modes: MODES };
  }
  return context;
};
